import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { ChevronDown, Mail } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type RowState = {
  fromText: string;
  replyTo: string;
  bccText: string;
};

function emptyRow(): RowState {
  return { fromText: '', replyTo: '', bccText: '' };
}

export default function MailRoutingSettingsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const isAdmin = auth.type === 'ADMIN';
  const perms = auth.permissions ?? [];
  const canEdit =
    isAdmin || perms.includes('MAIL_ROUTING:UPDATE');
  const [purposes, setPurposes] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [expandedPurpose, setExpandedPurpose] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/mail-routing`);
      if (!res.ok) throw new Error('load');
      const data = (await res.json()) as {
        purposes: string[];
        config: Record<
          string,
          { fromEmails?: string[]; replyTo?: string; bccEmails?: string[] }
        >;
      };
      setPurposes(data.purposes || []);
      const next: Record<string, RowState> = {};
      for (const p of data.purposes || []) {
        const c = data.config?.[p];
        next[p] = {
          fromText: (c?.fromEmails ?? []).join('\n'),
          replyTo: c?.replyTo ?? '',
          bccText: (c?.bccEmails ?? []).join('\n'),
        };
      }
      setRows(next);
    } catch {
      setError(t('backoffice.mail_routing_load_error'));
    } finally {
      setLoading(false);
    }
  }, [globalAuthorizedFetch, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const setRow = (purpose: string, patch: Partial<RowState>) => {
    setRows((prev) => ({
      ...prev,
      [purpose]: { ...(prev[purpose] ?? emptyRow()), ...patch },
    }));
    setSaved(false);
  };

  const buildPayload = () => {
    const config: Record<
      string,
      { fromEmails: string[]; replyTo?: string; bccEmails?: string[] }
    > = {};
    for (const p of purposes) {
      const r = rows[p] ?? emptyRow();
      const fromEmails = r.fromText
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes('@'));
      const bccEmails = r.bccText
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes('@'));
      const replyTo = r.replyTo.trim();
      if (
        fromEmails.length === 0 &&
        !replyTo &&
        bccEmails.length === 0
      ) {
        continue;
      }
      config[p] = { fromEmails };
      if (replyTo.includes('@')) config[p].replyTo = replyTo;
      if (bccEmails.length) config[p].bccEmails = bccEmails;
    }
    return config;
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/mail-routing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error('save');
      setSaved(true);
      await load();
    } catch {
      setError(t('backoffice.mail_routing_save_error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="mb-1 flex items-center gap-2 workspace-page-title">
            <Mail className="h-7 w-7 text-[#00b853]" strokeWidth={2} />
            {t('backoffice.mail_routing_title')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.mail_routing_subtitle')}
          </p>
        </section>

        {error ? (
          <div className="rounded-none border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}
        {saved ? (
          <div className="rounded-none border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            {t('backoffice.mail_routing_saved')}
          </div>
        ) : null}

        {!canEdit ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {t('backoffice.mail_routing_readonly')}
          </div>
        ) : null}

        <section className="space-y-4">
          <p className="text-xs text-slate-500">
            {t('backoffice.mail_routing_hint')}
          </p>

          {loading ? (
            <p className="text-sm text-slate-600">{t('common.loading')}</p>
          ) : (
            purposes.map((p) => {
              const r = rows[p] ?? emptyRow();
              const isExpanded = expandedPurpose === p;
              return (
                <div
                  key={p}
                  className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${
                    isExpanded ? '' : 'cursor-pointer hover:border-slate-300'
                  }`}
                  onClick={() => {
                    if (!isExpanded) setExpandedPurpose(p);
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedPurpose((prev) => (prev === p ? null : p));
                    }}
                    className="flex w-full items-center justify-between text-left"
                    aria-expanded={isExpanded}
                  >
                    <h2 className="text-sm font-semibold text-slate-900">
                      {t(`backoffice.mail_routing_purpose_${p}`, { defaultValue: p })}
                    </h2>
                    <ChevronDown
                      className={`h-4 w-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      strokeWidth={2}
                    />
                  </button>
                  {isExpanded ? (
                    <div className="mt-3 grid gap-4 sm:grid-cols-1">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600">
                          {t('backoffice.mail_routing_from')}
                        </label>
                        <textarea
                          value={r.fromText}
                          onChange={(e) => setRow(p, { fromText: e.target.value })}
                          rows={2}
                          disabled={!canEdit}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs disabled:cursor-not-allowed disabled:bg-slate-50"
                          placeholder="account@lewouhmart.com"
                        />
                        <p className="mt-1 text-[11px] text-slate-500">
                          {t('backoffice.mail_routing_from_help')}
                        </p>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600">
                          {t('backoffice.mail_routing_reply_to')}
                        </label>
                        <input
                          value={r.replyTo}
                          onChange={(e) => setRow(p, { replyTo: e.target.value })}
                          disabled={!canEdit}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs disabled:cursor-not-allowed disabled:bg-slate-50"
                          placeholder="noreply@lewouhmart.com"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600">
                          {t('backoffice.mail_routing_bcc')}
                        </label>
                        <textarea
                          value={r.bccText}
                          onChange={(e) => setRow(p, { bccText: e.target.value })}
                          rows={2}
                          disabled={!canEdit}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs disabled:cursor-not-allowed disabled:bg-slate-50"
                          placeholder="ops@lewouhmart.com"
                        />
                        <p className="mt-1 text-[11px] text-slate-500">
                          {t('backoffice.mail_routing_bcc_help')}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}

          {!loading ? (
            <button
              type="button"
              disabled={saving || !canEdit}
              onClick={() => void save()}
              className="rounded-full bg-[#00b853] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#009946] disabled:opacity-60"
            >
              {saving ? t('backoffice.mail_routing_saving') : t('backoffice.mail_routing_save')}
            </button>
          ) : null}
        </section>
      </main>
    </MainLayout>
  );
}
